<script setup>
import { nextTick, onMounted, ref, watch } from 'vue'
import {
  ClockIcon,
  CpuChipIcon,
  TagIcon,
  UserGroupIcon,
} from '@heroicons/vue/24/outline'
import { useI18n } from '../../i18n.js'

const { t } = useI18n()

const props = defineProps({
  controllerId: { type: Number, required: true },
  active: { type: String, required: true },
})

const nav = ref(null)

function revealActiveItem() {
  const item = nav.value?.querySelector('[aria-current="page"]')
  if (!item) return
  nav.value.scrollLeft = Math.max(0, item.offsetLeft - (nav.value.clientWidth - item.offsetWidth) / 2)
}

onMounted(() => nextTick(revealActiveItem))
watch(() => props.active, () => nextTick(revealActiveItem))

const items = [
  { key: 'devices', labelKey: 'pts2UiDevices', icon: CpuChipIcon, route: 'pts2-controller-detail' },
  { key: 'history', labelKey: 'pts2UiConfigurationHistory', icon: ClockIcon, route: 'pts2-configuration-history' },
  { key: 'prices', labelKey: 'pts2UiPrices', icon: TagIcon, route: 'pts2-controller-prices' },
  { key: 'operator-tags', labelKey: 'pts2UiOperatorTags', icon: UserGroupIcon, route: 'pts2-operator-tags' },
]
</script>

<template>
  <nav ref="nav" class="pts2-controller-nav" :aria-label="t('pts2UiControllerSections')">
    <template v-for="item in items" :key="item.key">
      <RouterLink
        v-if="item.route"
        :class="{ active: active === item.key }"
        :aria-current="active === item.key ? 'page' : undefined"
        :to="{ name: item.route, params: { controllerId } }"
      >
        <component :is="item.icon" aria-hidden="true" />
        <span>{{ t(item.labelKey) }}</span>
      </RouterLink>
      <span v-else aria-disabled="true">
        <component :is="item.icon" aria-hidden="true" />
        <span>{{ t(item.labelKey) }}</span>
      </span>
    </template>
  </nav>
</template>

<style scoped>
.pts2-controller-nav { display: flex; width: 100%; min-width: 0; max-width: 100%; min-height: 60px; align-items: stretch; gap: 12px; margin: 4px 0 18px; border-bottom: 1px solid #cad7dc; overflow-x: auto; scrollbar-width: thin; }
.pts2-controller-nav > a, .pts2-controller-nav > span { position: relative; display: inline-flex; min-width: max-content; align-items: center; gap: 9px; padding: 5px 14px 13px; border: 0; color: #405d6d; background: transparent; font-size: 15px; font-weight: 600; text-decoration: none; }
.pts2-controller-nav > a:hover { color: #006e82; background: #f0f7f8; }
.pts2-controller-nav > a.active { color: #00778c; }
.pts2-controller-nav > a.active::after { position: absolute; right: 0; bottom: -1px; left: 0; height: 3px; background: #00849a; content: ''; }
.pts2-controller-nav > span[aria-disabled='true'] { color: #788b94; cursor: default; }
.pts2-controller-nav svg { width: 22px; height: 22px; flex: 0 0 auto; }
.pts2-controller-nav a:focus-visible { border-radius: 4px; outline: 3px solid #7bc2ce; outline-offset: -3px; }
@media (max-width: 700px) {
  .pts2-controller-nav { min-height: 54px; gap: 4px; }
  .pts2-controller-nav > a, .pts2-controller-nav > span { padding-right: 12px; padding-left: 12px; font-size: 14px; }
}
</style>
